import { useState, useEffect, FC } from "react";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Button } from "@ssce/ui-shared-library";
import Accordion from "../Accordion";
import Typography from "@mui/material/Typography";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ClearIcon from "@mui/icons-material/Clear";
import ConfirmModal from "../ConfirmModal";
import { Permissions } from "../../../../common/dataTypes";
import { connect, ConnectedProps } from "react-redux";
import { AppState } from "../../../../redux/store";
import {
  fetcRolePermissions,
  fetcPermissions,
  updateRolePermissions,
  deleteRolePermissions,
} from "../../../../redux/rolesManagement/actions";
import Acl from "../../subcomponents/Acl";
import { useAclHooks } from "../../subcomponents/Acl/hooks";
import { PERMISSION_LIST } from "../../../../constants";

const mapStateToProps = (state: AppState) => ({
  selectedRole: state.rolesManagement.selectedRole,
  rolePermissions: state.rolesManagement.rolePermissions,
  permissionsList: state.rolesManagement.permissionsList,
});

const mapDispatchToProps = {
  fetcRolePermissions,
  fetcPermissions,
  deleteRolePermissions,
  updateRolePermissions,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type Props = ConnectedProps<typeof connector>;

const PermissionsManagement: FC<Props> = ({
  selectedRole,
  rolePermissions,
  permissionsList,
  fetcRolePermissions,
  fetcPermissions,
  deleteRolePermissions,
  updateRolePermissions,
}) => {
  const [selectedPermissions, setSelectedPermissions] = useState<Permissions[]>(
    [],
  );
  const [open, setOpen] = useState(false);
  const [deleteData, setDeleteData] = useState<Permissions>({
    id: "",
    name: "",
    description: "",
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleDeleteClick = () => {
    handleClose();
    deleteRolePermissions(selectedRole.id, [{ id: deleteData.id }]);
  };

  const handleApplyClick = () => {
    let payload = selectedPermissions.map((item) => {
      return { id: item.id };
    });
    setSelectedPermissions([]);
    updateRolePermissions(selectedRole.id, payload);
  };

  useEffect(() => {
    fetcRolePermissions(selectedRole.id);
    fetcPermissions();
  }, [fetcRolePermissions, fetcPermissions, selectedRole.id]);

  const allowPermissionDelete = useAclHooks(PERMISSION_LIST.ROLES_EXECUTE);
  const renderAssignedPermissions = () => {
    return rolePermissions.map((item) => {
      return (
        <Accordion
          key={item.name}
          name={item.name}
          iconHeader={allowPermissionDelete && <DeleteForeverIcon />}
          iconHeaderClickhandler={(e) => {
            e.stopPropagation();
            handleOpen();
            setDeleteData(item);
          }}
        >
          <Typography
            style={{ color: "#0A85C7", fontSize: 12, marginLeft: 20 }}
          >
            Description
          </Typography>
          <Typography
            style={{
              color: "#02273C",
              fontWeight: 400,
              fontSize: 14,
              marginLeft: 20,
            }}
          >
            {item.description}
          </Typography>
          <br></br>
        </Accordion>
      );
    });
  };

  const renderSelectedPermissions = () => {
    return selectedPermissions.map((item) => {
      return (
        <Accordion
          key={item.name}
          name={item.name}
          iconHeader={<ClearIcon />}
          iconHeaderClickhandler={(e) => {
            e.stopPropagation();
            setSelectedPermissions([
              ...selectedPermissions.filter((r) => item.id !== r.id),
            ]);
          }}
        >
          <Typography
            style={{ color: "#0A85C7", fontSize: 12, marginLeft: 20 }}
          >
            Description
          </Typography>
          <Typography
            style={{
              color: "#02273C",
              fontWeight: 400,
              fontSize: 14,
              marginLeft: 20,
            }}
          >
            {item.description}
          </Typography>
          <br></br>
        </Accordion>
      );
    });
  };

  return (
    <>
      <div style={{ width: "100%" }}>
        <Typography variant="h6">Permissions</Typography>
        <br></br>
        {renderAssignedPermissions()}
        <br></br>
      </div>
      <ConfirmModal
        open={open}
        title={"Please Confirm"}
        onRequestCancel={handleClose}
        onRequestConfirm={handleDeleteClick}
      >
        <Typography
          style={{
            margin: 2,
            fontSize: 20,
            fontWeight: 500,
            lineHeight: "1.5rem",
          }}
        >
          You are{" "}
          <span style={{ textDecoration: "underline", fontWeight: 900 }}>
            removing{" "}
          </span>{" "}
          the{" "}
          <span style={{ fontWeight: 700, color: "#0468B1" }}>
            {deleteData.name}
          </span>{" "}
          from the {selectedRole.name} role. You can re-add this{" "}
          {deleteData.name} if you change your mind.
          <div style={{ fontWeight: 900 }}>
            Are you sure you want to continue?
          </div>
        </Typography>
      </ConfirmModal>
      <Acl permission={PERMISSION_LIST.ROLES_WRITE}>
        <div style={{ marginBottom: "10%" }}>
          <Typography variant="h6">Assign Permissions</Typography>
          <br></br>
          <div style={{ border: "0.063rem solid #CBD9E1", borderRadius: 4 }}>
            <Stack
              spacing={2}
              sx={{
                margin: 2,
                marginBottom: "0.188rem",
                width: 570,
                backgroundColor: "#F6FDFF",
              }}
            >
              <Autocomplete
                multiple
                id="size-small-outlined-multi"
                size="medium"
                value={selectedPermissions}
                sx={{
                  "& .MuiChip-root": {
                    backgroundColor: "#B3D9EE",
                    color: "#075E8D",
                  },
                  "& .MuiChip-root .MuiChip-deleteIcon": { color: "#0A85C7" },
                }}
                onChange={(_, values) => {
                  setSelectedPermissions(values);
                }}
                options={permissionsList}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label="Permissions" placeholder="" />
                )}
              />
            </Stack>
            <p
              style={{
                marginLeft: 20,
                marginTop: 0,
                color: "#577687",
                backgroundColor: "#EDF4F8",
                fontSize: 12,
              }}
            >
              Please use the dropdown box above or type in a Permission name to
              select it.
            </p>
            <p
              style={{
                margin: "1rem 1.25rem",
                fontStyle: "normal",
                fontWeight: 600,
                fontSize: 18,
                textAlign: "left",
                color: "#02273C",
              }}
            >
              Permission Addition Details
            </p>
            {renderSelectedPermissions()}
            <div
              style={{
                width: "85%",
                marginLeft: 20,
                marginBottom: 20,
                display: "flex",
                flexDirection: "row",
              }}
            >
              {" "}
              <br></br>
              <Button
                id={"apply"}
                onClick={handleApplyClick}
                variant="default"
                size="medium"
              >
                Apply changes
              </Button>
              <Button
                id={"cancel"}
                onClick={() => setSelectedPermissions([])}
                variant="text"
                size="medium"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </Acl>
    </>
  );
};

export default connector(PermissionsManagement);
